import type { FrameRecords, NearMiss, SideDisplayConfig } from "../types/api";

// Project the near-miss queue into a FrameRecords so the Suggested-matches TABLE view can
// be the ordinary ColumnFilterGrid every other table in the app already is — per-column
// filters, paste-a-column-from-Excel search, drag-select totals, pinning, and (the point)
// "Select all N matching rows" over the whole filtered set rather than the visible page.
//
// One row per SUGGESTED PAIR, not per ledger row: the unit finance accepts or rejects is
// the pair, so the tick, the filter and the bulk action all have to key on it. `_sig` is
// therefore the pair key (`${cri_sig}⟷${insurer_sig}`) — the SAME string the list view's
// selection/dismissal sets use, so the two views share one selection with no translation.
//
// Columns are the pair's own comparison fields first (reason/tier/amounts/gap), then EVERY
// raw column of both underlying ledger rows. Finance identifies a transaction by whatever
// its own SOA happens to carry, so curating a subset here would hide the very field they
// filter on; this mirrors allColumns()'s "curated first, then everything populated" rule
// that the ledger grids already follow. Keys are composite (meta::/cri::/ins::) so the
// grid draws the Match | CRI | insurer bands every other paired table has, and the side
// is read off the band rather than repeated into each header.

// Separator between the two legs' signatures in the pair key. MUST stay identical to the
// key ManualMatchTab builds for `selected` / `dismissed`, or a tick in one view is invisible
// in the other.
export const PAIR_SEP = "⟷";

export function pairKeyOf(s: NearMiss): string {
  return `${s.cri_sig}${PAIR_SEP}${s.insurer_sig}`;
}

// Pair-level columns, in the order they lead the table. These are the fields a user
// triages on — why it was suggested, how far apart the amounts are — so they come before
// the raw ledger detail rather than after it.
const PAIR_COLS = [
  "reason", "tier", "cri_amount", "insurer_amount", "amount_gap", "gap_pct", "conflict",
] as const;

// Band prefix of the pair-level columns (lib/columnGroups::sideOf -> the "Match" band).
const META = "meta::";

const PAIR_LABELS: Record<string, string> = {
  reason: "Reason",
  tier: "Tier",
  cri_amount: "CRI Amount",
  insurer_amount: "Insurer Amount",
  amount_gap: "Gap",
  gap_pct: "Gap %",
  conflict: "Conflict",
};

// Value of the `conflict` column for a pair whose two legs are claimed by no other
// suggestion. A real string (not blank) so the column filter offers it as a choice and
// "uncontested only" is one click.
export const NO_CONFLICT = "—";

// Raw ledger columns that carry no meaning to a person and would only widen the table:
// the signature itself (it IS the row key, shown as the pair key), and the matcher's own
// pointer bookkeeping. Everything else the Excel file carried is kept — that is the ask.
const LEG_SKIP = new Set(["_sig", "_pol", "matched_row_id", "match_group_id"]);

function titleCase(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

type Row = FrameRecords["rows"][number];

// Leg columns, resolved once. Each entry maps a source column index to its projected key
// (`${side}::${raw key}`, unique by construction) and its header label.
//
// Labels must be distinguishable too: the insurer book carries the raw SOA header AND the
// engine's normalised twin ("Doc Date" + "doc_date", "Debit" + "debit", "Narration",
// "Credit" on Dubai), and title-casing flattens each pair onto one label. Both are kept
// (their values can differ, and dropping either would hide a column the file carried), so
// the later one is qualified by its true source key rather than renamed to something
// invented.
export function legColumns(frame: FrameRecords, side: "cri" | "ins") {
  const used = new Set<string>();
  return frame.columns
    .map((key, index) => ({ key, index }))
    .filter((c) => !LEG_SKIP.has(c.key))
    .map((c) => {
      let label = titleCase(c.key);
      if (used.has(label)) label = `${titleCase(c.key)} (${c.key})`;
      used.add(label);
      return { ...c, out: `${side}::${c.key}`, label };
    });
}

// Row lookup by signature for one frame, built once per projection (not per row) — a
// per-row .find over a 69k-row Dubai frame would be O(pairs x rows).
export function sigIndex(frame: FrameRecords): Map<string, Row> {
  const si = frame.columns.indexOf("_sig");
  const m = new Map<string, Row>();
  if (si < 0) return m;
  for (const r of frame.rows) {
    const k = String(r[si] ?? "");
    if (k && !m.has(k)) m.set(k, r);
  }
  return m;
}

export interface SuggestionFrame {
  frame: FrameRecords;
  // Display config naming every column in order with its bare label ("Gap %", "Policy
  // No"), pair-level columns ahead of the ledger detail; the band names the side.
  config: SideDisplayConfig;
}

// CONTESTED SUGGESTIONS.
//
// The near-miss queue dedupes on the PAIR (cri_sig, insurer_sig) and never on either leg
// alone, so one ledger row can legitimately appear in several suggestions — on Salama,
// three separate CRI rows each claim insurer row `DN (ISSUE)|'6967/2025|AL SHIRAWI...`.
// The engine matches at most ONE of them: _apply_manual_matches consumes a leg
// (`ins_map[sig] = None`) as soon as a group takes it, and every later group naming that
// row is refused with "no matching unmatched Insurer row". That refusal is CORRECT —
// matching one insurer row against three CRI rows would double-count the money — but it
// means a bulk Accept over a filtered set silently applies fewer matches than it claimed.
//
// Which competitor is the right one is a judgement about the underlying documents, so
// nothing here guesses. It only makes the competition VISIBLE, so the user decides.

/** How many suggestions in the queue claim each CRI and each insurer signature. */
function legCounts(suggestions: NearMiss[]) {
  const cri = new Map<string, number>();
  const ins = new Map<string, number>();
  for (const s of suggestions) {
    cri.set(s.cri_sig, (cri.get(s.cri_sig) ?? 0) + 1);
    ins.set(s.insurer_sig, (ins.get(s.insurer_sig) ?? 0) + 1);
  }
  return { cri, ins };
}

/** True when either leg of `s` is also claimed by another suggestion in `suggestions`. */
export function isContested(s: NearMiss, suggestions: NearMiss[]): boolean {
  const { cri, ins } = legCounts(suggestions);
  return (cri.get(s.cri_sig) ?? 0) > 1 || (ins.get(s.insurer_sig) ?? 0) > 1;
}

/**
 * Split a chosen set into the pairs that can all be applied together and those that
 * cannot.
 *
 * `ok` is a maximal set with no two entries sharing a leg — accepting it applies in full,
 * with nothing silently dropped. `contested` holds every pair left out because an earlier
 * entry already claimed one of its rows, grouped under the signature they compete for.
 *
 * Order is preserved, so "first wins" matches what the engine itself does when handed the
 * whole list: the engine consumes legs in list order, so `ok` is exactly the subset it
 * would have applied anyway — this only stops the rest from being sent and failing.
 */
export function splitContested(chosen: NearMiss[]): {
  ok: NearMiss[];
  contested: { key: string; side: "CRI" | "Insurer"; rivals: NearMiss[] }[];
} {
  const takenCri = new Set<string>();
  const takenIns = new Set<string>();
  const ok: NearMiss[] = [];
  // Losers, indexed by the signature they lost on, with the winner listed first so the
  // user compares like with like.
  const groups = new Map<string, { key: string; side: "CRI" | "Insurer"; rivals: NearMiss[] }>();
  const winnerFor = new Map<string, NearMiss>();

  for (const s of chosen) {
    const criTaken = takenCri.has(s.cri_sig);
    const insTaken = takenIns.has(s.insurer_sig);
    if (!criTaken && !insTaken) {
      ok.push(s);
      takenCri.add(s.cri_sig);
      takenIns.add(s.insurer_sig);
      winnerFor.set(s.cri_sig, s);
      winnerFor.set(s.insurer_sig, s);
      continue;
    }
    // Report against the leg that actually blocked it (insurer checked second only so a
    // pair blocked on both is attributed to its CRI row, deterministically).
    const key = criTaken ? s.cri_sig : s.insurer_sig;
    const side: "CRI" | "Insurer" = criTaken ? "CRI" : "Insurer";
    let g = groups.get(key);
    if (!g) {
      const won = winnerFor.get(key);
      g = { key, side, rivals: won ? [won] : [] };
      groups.set(key, g);
    }
    g.rivals.push(s);
  }
  return { ok, contested: [...groups.values()] };
}

/**
 * Build the suggestion grid's frame + display config.
 *
 * `gap_pct` is the gap as a percentage of the larger leg — the scale-invariant read the
 * engine itself gates on (_NEAR_MISS_MAX_FRAC). A 2.00 gap on a 50 AED document is a
 * different judgement from 2.00 on 500k, and sorting/filtering on the absolute gap alone
 * cannot express that, so it is a first-class column. Rounded to 2dp for display; null
 * (blank) when both legs are zero, never a fabricated 0.
 */
export function buildSuggestionFrame(
  suggestions: NearMiss[],
  criFrame: FrameRecords,
  insFrame: FrameRecords,
): SuggestionFrame {
  const criBySig = sigIndex(criFrame);
  const insBySig = sigIndex(insFrame);

  const criCols = legColumns(criFrame, "cri");
  const insCols = legColumns(insFrame, "ins");

  // Every display column is named in the config, so the grid keeps this exact order and
  // shows bare labels under each side's band.
  const display = [
    ...PAIR_COLS.map((c) => META + c),
    ...criCols.map((c) => c.out),
    ...insCols.map((c) => c.out),
  ];
  const columns = [...display, "_sig"];

  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];

  // Conflict counts run over the WHOLE queue, never the filtered view: a rival hidden by
  // the current filter still consumes the row, so counting only what is on screen would
  // call a contested pair clean.
  const counts = legCounts(suggestions);
  // Rank among the rivals for a leg, in queue order (closest gap first), so the label
  // reads "1 of 3" / "2 of 3" and the user can see which the engine would take.
  const seenCri = new Map<string, number>();
  const seenIns = new Map<string, number>();

  suggestions.forEach((s, i) => {
    const mag = Math.max(Math.abs(s.cri_amount), Math.abs(s.insurer_amount));
    const gapPct = mag > 0 ? Math.round((s.amount_gap / mag) * 10000) / 100 : null;
    const criRow = criBySig.get(s.cri_sig);
    const insRow = insBySig.get(s.insurer_sig);
    const nCri = counts.cri.get(s.cri_sig) ?? 0;
    const nIns = counts.ins.get(s.insurer_sig) ?? 0;
    // Attribute to whichever leg is contested (CRI first, deterministically, matching
    // splitContested); a pair contested on both reports its CRI competition.
    let conflict = NO_CONFLICT;
    if (nCri > 1) {
      const r = (seenCri.get(s.cri_sig) ?? 0) + 1;
      seenCri.set(s.cri_sig, r);
      conflict = `CRI ${r} of ${nCri}`;
    } else if (nIns > 1) {
      const r = (seenIns.get(s.insurer_sig) ?? 0) + 1;
      seenIns.set(s.insurer_sig, r);
      conflict = `Insurer ${r} of ${nIns}`;
    }
    rows.push([
      s.reason, s.tier, s.cri_amount, s.insurer_amount, s.amount_gap, gapPct, conflict,
      // A leg whose row is not in the passed frame yields blanks rather than dropping the
      // pair: the suggestion is still real and still acceptable, and silently omitting it
      // would make the table disagree with the list view's count.
      ...criCols.map((c) => (criRow ? criRow[c.index] : null)),
      ...insCols.map((c) => (insRow ? insRow[c.index] : null)),
      pairKeyOf(s),
    ]);
    indices.push(i);
  });

  return {
    frame: { columns, rows, indices, total_rows: rows.length },
    config: {
      columns: display,
      renames: Object.fromEntries([
        ...PAIR_COLS.map((c) => [META + c, PAIR_LABELS[c]]),
        ...criCols.map((c) => [c.out, c.label]),
        ...insCols.map((c) => [c.out, c.label]),
      ]),
    },
  };
}
